import { useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SEO, getFAQSchema } from "../components/SEO";

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const faqCategories = [
    {
      category: "General Questions",
      questions: [
        {
          question: "What is PECB certification?",
          answer: "PECB (Professional Evaluation and Certification Board) is a certification body that provides certification services for persons, management systems, and products. Their certifications are internationally recognized and demonstrate your competence in specific management system standards like ISO 9001, ISO 22000, ISO 27001, and more."
        },
        {
          question: "Why should I get certified?",
          answer: "PECB certification validates your expertise and knowledge in specific management systems. It enhances your career prospects, increases your earning potential, and demonstrates your commitment to professional excellence. Certified professionals are often preferred by employers and clients worldwide."
        },
        {
          question: "How long are PECB certifications valid?",
          answer: "PECB certifications are typically valid for 3 years. To maintain your certification, you need to demonstrate ongoing professional development and may need to pass a recertification exam or meet continuing education requirements."
        },
      ]
    },
    {
      category: "Course Information",
      questions: [
        {
          question: "What delivery formats are available?",
          answer: "We offer three flexible delivery formats: Self-Study (learn at your own pace with comprehensive materials), Online Live (interactive sessions with expert instructors), and E-Learning (flexible online modules you can complete anytime). Each format provides the same high-quality content and prepares you for certification."
        },
        {
          question: "What is the difference between Foundation, Lead Implementer, and Lead Auditor?",
          answer: "Foundation courses provide basic understanding of management systems and are ideal for beginners. Lead Implementer courses prepare you to implement and manage systems within organizations. Lead Auditor courses qualify you to conduct audits and assessments. Each level builds on the previous one and targets different career paths."
        },
        {
          question: "Do I need prerequisites for the courses?",
          answer: "Prerequisites vary by course level. Foundation courses typically have no prerequisites. Lead Implementer and Lead Auditor courses may require prior knowledge of the relevant management system or completion of a Foundation course. Check individual course pages for specific requirements."
        },
        {
          question: "What materials are included with the course?",
          answer: "All courses include comprehensive study materials, presentation slides, case studies, sample exam questions, and a certificate of completion. Online Live courses also include recorded sessions for review. Additional resources may include standard references and implementation templates."
        },
      ]
    },
    {
      category: "Registration & Payment",
      questions: [
        {
          question: "How do I register for a course?",
          answer: "Registration is simple: browse our course catalog, select your desired course, choose your preferred delivery format, and click 'Enroll Now'. Complete the checkout process with your details and payment information. You'll receive immediate confirmation and course access details via email."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) through our secure Square payment gateway. Corporate clients can also request invoicing for group enrollments."
        },
        {
          question: "Can I get a refund if I can't attend?",
          answer: "We offer a full refund if you cancel at least 14 days before the course start date for Online Live courses. Self-Study and E-Learning courses can be refunded within 7 days of purchase if you haven't accessed more than 20% of the content. Please contact us for specific situations."
        },
        {
          question: "Do you offer corporate training or group discounts?",
          answer: "Yes! We offer special pricing for corporate groups of 5 or more participants. We can also customize training programs to meet your organization's specific needs. Contact us for a custom quote and to discuss your requirements."
        },
      ]
    },
    {
      category: "Exams & Certification",
      questions: [
        {
          question: "How do I schedule my certification exam?",
          answer: "After completing your course, you'll receive instructions for registering with PECB to schedule your certification exam. Exams can be taken online or at authorized testing centers. We provide guidance throughout the scheduling process."
        },
        {
          question: "What is the exam format?",
          answer: "PECB exams typically consist of multiple-choice questions and may include essay questions for higher-level certifications. Foundation exams are usually 1 hour with 40 questions. Lead Implementer and Lead Auditor exams are longer and may include practical components."
        },
        {
          question: "What happens if I don't pass the exam?",
          answer: "If you don't pass your first attempt, you can retake the exam after a waiting period (typically 30 days). Our courses include exam preparation tips and practice questions to maximize your chances of success. Many participants find our pass rate of 98% reassuring."
        },
        {
          question: "When will I receive my certificate?",
          answer: "After passing your exam and submitting any required documentation to PECB, you'll typically receive your digital certificate within 2-4 weeks. Physical certificates can be requested for an additional fee."
        },
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "What technical requirements do I need for online courses?",
          answer: "For online courses, you'll need a computer with a stable internet connection, a modern web browser (Chrome, Firefox, Safari, or Edge), and speakers or headphones. For Online Live sessions, a microphone is recommended for participation. Mobile devices can be used for some content but a computer is recommended for the best experience."
        },
        {
          question: "How long do I have access to course materials?",
          answer: "Self-Study and E-Learning course materials are accessible for 12 months from your enrollment date. Online Live participants retain access to recorded sessions and materials for 6 months after the course completion. You can download certain materials for permanent reference."
        },
        {
          question: "Who do I contact for technical issues?",
          answer: "For any technical issues, contact our support team at support@comply-cms.com or call us during business hours. We typically respond to support requests within 24 hours and are committed to resolving issues quickly so your learning isn't disrupted."
        },
      ]
    },
  ];

  const filteredFAQs = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  // Flatten all FAQs for structured data
  const allFAQs = faqCategories.flatMap(cat => cat.questions);

  return (
    <div className="min-h-screen">
      <SEO
        title="FAQ | PECB Training Questions Answered"
        description="Find answers to common questions about PECB certification, ISO training courses, exam requirements, corporate discounts, and payment options. Get help with your certification journey."
        url="/faq"
        keywords={[
          "PECB certification FAQ",
          "ISO training questions",
          "Lead Auditor exam",
          "PECB course requirements",
          "certification validity",
          "corporate training FAQ"
        ]}
        structuredData={getFAQSchema(allFAQs)}
      />
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-accent font-medium text-sm uppercase tracking-widest mb-4 block">
              Help Center
            </span>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white tracking-tight mb-6" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-white/80 mb-8">
              Find answers to common questions about our courses, certifications, and services.
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg bg-white"
                data-testid="faq-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No questions found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {filteredFAQs.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h2 className="font-heading font-bold text-2xl mb-6">{category.category}</h2>
                  <Accordion type="single" collapsible className="space-y-4">
                    {category.questions.map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`${categoryIndex}-${index}`}
                        className="bg-white border border-border rounded-lg px-6"
                        data-testid={`faq-item-${categoryIndex}-${index}`}
                      >
                        <AccordionTrigger className="text-left font-medium hover:no-underline py-4">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground pb-4">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          )}

          {/* Contact CTA */}
          <div className="mt-16 text-center bg-white rounded-xl p-8 border border-border">
            <h3 className="font-heading font-bold text-xl mb-2">Still have questions?</h3>
            <p className="text-muted-foreground mb-4">
              Can't find what you're looking for? Our team is here to help.
            </p>
            <a href="/contact" className="text-brand hover:underline font-medium">
              Contact Us →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
